import { HStack, VStack } from '@chakra-ui/react';
import { CompanyAssessment, CompanyAssessmentResults, CompanyDetails } from 'models';
import { Typography } from 'Tokens';
import { ExcelFileIcon } from 'Tokens/Icons/FileIcons';
import { useTranslation } from 'utils/translation';
import mixpanel from 'mixpanel-browser';
import { TRACKING_EVENTS } from 'utils/mixpanel';
import { captureException } from '@sentry/browser';
import { stringToYear } from 'utils/date';
import { generateGraphicsTable, GraphicsTableData } from './graphic-generator';

export const GraphicsGenerator = ({
  company,
  assessment,
  cAssessmentResults,
}: {
  company?: CompanyDetails;
  isGroup?: boolean;
  assessment?: CompanyAssessment;
  cAssessmentResults?: CompanyAssessmentResults;
}) => {
  const { t } = useTranslation('assessment');

  const generate = () => {
    if (!company || !assessment || !cAssessmentResults?.cachedResult) {
      console.error('Missing data to generate graphics report.');
      return;
    }

    try {
      const { score, financials } = cAssessmentResults.cachedResult;
      const graphicsData: GraphicsTableData = {
        companyName: company.name ?? 'Company',
        reportingYear: stringToYear(assessment.startDate) || new Date().getUTCFullYear(),
        currency: company.currency ?? 'EUR',
        fullScore: {
          Turnover: {
            total: financials?.revenue?.total ?? null,
            eligible: score?.revenue?.eligible ?? null,
            aligned: score?.revenue?.aligned ?? null,
            inProgress: score?.revenue?.inProgress ?? null,
            enabling: score?.revenue?.enabling ?? null,
            transitional: score?.revenue?.transitional ?? null,
          },
          CapEx: {
            total: financials?.capex?.total ?? null,
            eligible: score?.capex?.eligible ?? null,
            aligned: score?.capex?.aligned ?? null,
            inProgress: score?.capex?.inProgress ?? null,
            enabling: score?.capex?.enabling ?? null,
            transitional: score?.capex?.transitional ?? null,
          },
          OpEx: {
            total: financials?.opex?.total ?? null,
            eligible: score?.opex?.eligible ?? null,
            aligned: score?.opex?.aligned ?? null,
            inProgress: score?.opex?.inProgress ?? null,
            enabling: score?.opex?.enabling ?? null,
            transitional: score?.opex?.transitional ?? null,
          },
        },
      };

      generateGraphicsTable(graphicsData);
      mixpanel.track(TRACKING_EVENTS.ASSESSMENTS.GRAPHICS_DOWNLOAD);
    } catch (error) {
      captureException(error);
    }
  };

  return (
    <HStack w="100%" spacing="10.33px" onClick={generate}>
      <ExcelFileIcon />
      <VStack alignItems="start" spacing="0px">
        <Typography variant="bodyStrong">Graphics</Typography>
        <Typography variant="detail">{t('assessment:export.euTable.doc')}</Typography>
      </VStack>
    </HStack>
  );
};
